import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../utils/api';
import { isPosModuleEnabled } from '../config/featureFlags';
import '../App.css';
import './PosMenuAdmin.css';

type PosSite = { siteId: string; label: string };

type Overview = {
  settings: {
    siteId: string;
    taxRatePercent: number | null;
    menuVersion: number;
    updatedAt: string | null;
  };
  counts: {
    menus: number;
    items: number;
    modifierGroups: number;
    prepStations: number;
  };
};

type MenuRow = {
  id: number;
  name: string;
  sortOrder: number;
  active: boolean;
  groups: { id: number; name: string; itemCount: number; active: boolean }[];
};

type NamedRef = { id: number; name: string };

type MenuItemRow = {
  id: number;
  menuId: number;
  menuName: string;
  menuGroupId: number;
  groupName: string;
  itemId: number;
  name: string;
  price: number;
  openPrice: boolean;
  taxable: boolean;
  eightySixed: boolean;
  visibleOnPos: boolean;
  active: boolean;
  modifierGroups: NamedRef[];
  prepStations: NamedRef[];
};

type ModifierGroupRow = {
  id: number;
  name: string;
  minSelections: number;
  maxSelections: number | null;
  requiredMode: string | null;
  multiSelect: boolean;
  optionCount: number;
  active: boolean;
};

type PrepStationRow = {
  id: number;
  name: string;
  printerId: number | null;
  printerName: string | null;
  printerHostIp: string | null;
  active: boolean;
};

type PrinterRow = {
  id: number;
  name: string;
  hostIp: string | null;
  port: number;
  active: boolean;
};

type EditDraft = {
  price: string;
  taxable: boolean;
  eightySixed: boolean;
  active: boolean;
  visibleOnPos: boolean;
};

function formatPrice(n: number) {
  if (!Number.isFinite(n)) return '—';
  return n % 1 === 0 ? String(n) : n.toFixed(2);
}

function statusChips(row: MenuItemRow): { key: string; label: string; tone: string }[] {
  const chips: { key: string; label: string; tone: string }[] = [];
  if (row.eightySixed) chips.push({ key: '86', label: '86', tone: 'warn' });
  if (!row.taxable) chips.push({ key: 'tax', label: 'Non-taxable', tone: 'mute' });
  if (!row.active) chips.push({ key: 'off', label: 'Inactive', tone: 'mute' });
  if (!row.visibleOnPos) chips.push({ key: 'pos', label: 'Hidden on POS', tone: 'mute' });
  if (row.openPrice) chips.push({ key: 'open', label: 'Open price', tone: 'info' });
  return chips.slice(0, 3);
}

const PosMenuAdmin: React.FC = () => {
  const [sites, setSites] = useState<PosSite[]>([]);
  const [siteId, setSiteId] = useState('TAPROOM-MADISON');
  const [overview, setOverview] = useState<Overview | null>(null);
  const [menus, setMenus] = useState<MenuRow[]>([]);
  const [items, setItems] = useState<MenuItemRow[]>([]);
  const [mods, setMods] = useState<ModifierGroupRow[]>([]);
  const [stations, setStations] = useState<PrepStationRow[]>([]);
  const [printers, setPrinters] = useState<PrinterRow[]>([]);
  const [selectedMenuId, setSelectedMenuId] = useState<number | ''>('');
  const [itemQuery, setItemQuery] = useState('');
  const [taxInput, setTaxInput] = useState('');
  const [printerForm, setPrinterForm] = useState({ name: '', hostIp: '', port: '9100' });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [importResult, setImportResult] = useState<unknown>(null);
  const [editing, setEditing] = useState<MenuItemRow | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);

  const loadSites = useCallback(async () => {
    const res = await apiFetch('/api/pos/status');
    const status = await res.json();
    setSites(Array.isArray(status.sites) ? status.sites : []);
  }, []);

  const loadSite = useCallback(async () => {
    setError(null);
    try {
      const qs = `siteId=${encodeURIComponent(siteId)}`;
      const [ovRes, menusRes, itemsRes, modsRes, stationsRes, printersRes] = await Promise.all([
        apiFetch(`/api/pos/menu/overview?${qs}`),
        apiFetch(`/api/pos/menus?${qs}`),
        apiFetch(
          `/api/pos/menu-items?${qs}${selectedMenuId ? `&menuId=${selectedMenuId}` : ''}${
            itemQuery.trim() ? `&q=${encodeURIComponent(itemQuery.trim())}` : ''
          }`
        ),
        apiFetch(`/api/pos/modifier-groups?${qs}`),
        apiFetch(`/api/pos/prep-stations?${qs}`),
        apiFetch(`/api/pos/printers?${qs}`),
      ]);
      const ov = await ovRes.json();
      const menuRows = await menusRes.json();
      const itemRows = await itemsRes.json();
      const modRows = await modsRes.json();
      const stationRows = await stationsRes.json();
      const printerRows = await printersRes.json();
      setOverview(ov);
      setTaxInput(
        ov?.settings?.taxRatePercent == null ? '' : String(ov.settings.taxRatePercent)
      );
      setMenus(Array.isArray(menuRows) ? menuRows : []);
      setItems(
        Array.isArray(itemRows)
          ? itemRows.map((r: MenuItemRow) => ({
              ...r,
              modifierGroups: Array.isArray(r.modifierGroups) ? r.modifierGroups : [],
              prepStations: Array.isArray(r.prepStations) ? r.prepStations : [],
            }))
          : []
      );
      setMods(Array.isArray(modRows) ? modRows : []);
      setStations(Array.isArray(stationRows) ? stationRows : []);
      setPrinters(Array.isArray(printerRows) ? printerRows : []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load menu admin');
    }
  }, [siteId, selectedMenuId, itemQuery]);

  useEffect(() => {
    if (isPosModuleEnabled()) {
      loadSites().catch((err) =>
        setError(err instanceof Error ? err.message : 'Failed to load POS status')
      );
    }
  }, [loadSites]);

  useEffect(() => {
    if (isPosModuleEnabled()) {
      loadSite();
    }
  }, [loadSite]);

  if (!isPosModuleEnabled()) {
    return (
      <div className="page-container container">
        <h2>POS Menus</h2>
        <p>
          The POS module is turned off. Set POS_MODULE=1 (and REACT_APP_POS_MODULE=1 for the web
          build) to enable it.
        </p>
      </div>
    );
  }

  const siteOptions =
    sites.length > 0
      ? sites
      : [
          { siteId: 'TAPROOM-MADISON', label: 'Madison' },
          { siteId: 'TAPROOM-ATHENS', label: 'Athens' },
        ];

  const openEdit = (row: MenuItemRow) => {
    setEditing(row);
    setDraft({
      price: String(row.price),
      taxable: row.taxable,
      eightySixed: row.eightySixed,
      active: row.active,
      visibleOnPos: row.visibleOnPos,
    });
    setError(null);
    setSuccess(null);
  };

  const closeEdit = () => {
    setEditing(null);
    setDraft(null);
  };

  const saveEdit = async () => {
    if (!editing || !draft) return;
    const price = Number(draft.price);
    if (!Number.isFinite(price) || price < 0) {
      setError('Price must be a non-negative number');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/pos/menu-items/${editing.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price,
          active: draft.active,
          visibleOnPos: draft.visibleOnPos,
        }),
      });
      await apiFetch(`/api/pos/items/${editing.itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          taxable: draft.taxable,
          eightySixed: draft.eightySixed,
        }),
      });
      setSuccess('Item updated');
      closeEdit();
      await loadSite();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save item');
    } finally {
      setBusy(false);
    }
  };

  const saveTax = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const taxRatePercent = taxInput.trim() === '' ? null : Number(taxInput);
      await apiFetch(`/api/pos/settings/${encodeURIComponent(siteId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taxRatePercent }),
      });
      setSuccess('Tax rate saved');
      await loadSite();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save tax rate');
    } finally {
      setBusy(false);
    }
  };

  const runImport = async (dryRun: boolean) => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    setImportResult(null);
    try {
      const res = await apiFetch('/api/pos/menu/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun }),
      });
      const body = await res.json();
      setImportResult(body);
      setSuccess(dryRun ? 'Dry-run complete' : 'Toast menu import complete');
      if (!dryRun) await loadSite();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const patchMenuItem = async (row: MenuItemRow, patch: Partial<MenuItemRow>) => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/pos/menu-items/${row.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      await loadSite();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update menu item');
    } finally {
      setBusy(false);
    }
  };

  const patchItemFlags = async (row: MenuItemRow, patch: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/pos/items/${row.itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      await loadSite();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update item');
    } finally {
      setBusy(false);
    }
  };

  const createPrinter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!printerForm.name.trim()) {
      setError('Printer name is required');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiFetch('/api/pos/printers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          name: printerForm.name.trim(),
          hostIp: printerForm.hostIp.trim() || null,
          port: Number(printerForm.port) || 9100,
        }),
      });
      setPrinterForm({ name: '', hostIp: '', port: '9100' });
      setSuccess('Printer saved');
      await loadSite();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save printer');
    } finally {
      setBusy(false);
    }
  };

  const savePrinterIp = async (row: PrinterRow, hostIp: string) => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/pos/printers/${row.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hostIp: hostIp.trim() || null }),
      });
      await loadSite();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update printer');
    } finally {
      setBusy(false);
    }
  };

  const linkStationPrinter = async (stationId: number, printerId: string) => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/pos/prep-stations/${stationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          printerId: printerId === '' ? null : Number(printerId),
        }),
      });
      await loadSite();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update prep station');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-container container pos-menu-admin">
      <h2>POS Menus</h2>
      <p className="text-muted">
        Browse and edit taproom menus, prices, modifiers, prep stations, and kitchen printers.
        Madison tax defaults to 9% on the ticket subtotal before tip; Athens can be set when known.
        Import is idempotent and keyed on Toast GUIDs so reruns update instead of duplicating.
      </p>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="row g-2 align-items-end" style={{ marginBottom: 24 }}>
        <div className="col-md-3">
          <label className="form-label">Site</label>
          <select
            className="form-select"
            value={siteId}
            onChange={(e) => {
              setSelectedMenuId('');
              setSiteId(e.target.value);
            }}
          >
            {siteOptions.map((s) => (
              <option key={s.siteId} value={s.siteId}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">Tax rate %</label>
          <input
            className="form-control"
            value={taxInput}
            onChange={(e) => setTaxInput(e.target.value)}
            placeholder="e.g. 9"
            inputMode="decimal"
          />
        </div>
        <div className="col-md-2">
          <button type="button" className="btn btn-secondary w-100" disabled={busy} onClick={saveTax}>
            Save tax
          </button>
        </div>
        <div className="col-md-5 text-muted" style={{ paddingBottom: 6 }}>
          {overview
            ? `Menu version ${overview.settings.menuVersion} · ${overview.counts.menus} menus · ${overview.counts.items} items · ${overview.counts.modifierGroups} modifier groups · ${overview.counts.prepStations} prep stations`
            : 'Loading…'}
        </div>
      </div>

      <section style={{ marginBottom: 36 }}>
        <h3>Toast import</h3>
        <p className="text-muted">
          Loads the committed Toast CSV export under <code>fixtures/toast-menu</code> for Madison and
          Athens. Dry-run reports counts only; Import writes/updates the catalog.
        </p>
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={busy}
            onClick={() => runImport(true)}
          >
            Dry-run import
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => runImport(false)}
          >
            Run import
          </button>
        </div>
        {importResult != null && (
          <pre
            style={{
              marginTop: 12,
              maxHeight: 280,
              overflow: 'auto',
              background: '#f8f9fa',
              padding: 12,
              fontSize: 12,
            }}
          >
            {JSON.stringify(importResult, null, 2)}
          </pre>
        )}
      </section>

      <section style={{ marginBottom: 36 }}>
        <h3>Menus</h3>
        <div className="table-responsive">
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Menu</th>
                <th>Groups</th>
                <th>Items (active)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {menus.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td>{m.groups.length}</td>
                  <td>{m.groups.reduce((n, g) => n + Number(g.itemCount || 0), 0)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setSelectedMenuId(m.id)}
                    >
                      Filter items
                    </button>
                  </td>
                </tr>
              ))}
              {!menus.length && (
                <tr>
                  <td colSpan={4} className="text-muted">
                    No menus yet — run the Toast import.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section style={{ marginBottom: 36 }}>
        <h3>Menu items</h3>
        <p className="text-muted d-md-none" style={{ fontSize: 13 }}>
          Tap a row to edit price, tax, 86, and active flags.
        </p>
        <div className="row g-2 align-items-end pos-menu-item-filters">
          <div className="col-md-3">
            <label className="form-label">Filter by menu</label>
            <select
              className="form-select"
              value={selectedMenuId}
              onChange={(e) =>
                setSelectedMenuId(e.target.value ? Number(e.target.value) : '')
              }
            >
              <option value="">All menus</option>
              {menus.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Search name</label>
            <input
              className="form-control"
              value={itemQuery}
              onChange={(e) => setItemQuery(e.target.value)}
              placeholder="Item name"
            />
          </div>
        </div>

        {/* Phone: compact single-row list */}
        <div className="pos-menu-items-mobile" data-testid="pos-menu-items-mobile">
          {items.map((row) => {
            const chips = statusChips(row);
            return (
              <button
                type="button"
                key={row.id}
                className="pos-menu-item-row"
                onClick={() => openEdit(row)}
                disabled={busy}
              >
                <span className="pos-menu-item-row__main">
                  <span className="pos-menu-item-row__name">{row.name}</span>
                  <span className="pos-menu-item-row__meta">
                    {row.menuName} · {row.groupName}
                  </span>
                </span>
                <span className="pos-menu-item-row__price">${formatPrice(row.price)}</span>
                {chips.length > 0 && (
                  <span className="pos-menu-item-row__chips">
                    {chips.map((c) => (
                      <span
                        key={c.key}
                        className={`pos-menu-chip pos-menu-chip--${c.tone}`}
                      >
                        {c.label}
                      </span>
                    ))}
                  </span>
                )}
                <span className="pos-menu-item-row__chevron" aria-hidden>
                  ›
                </span>
              </button>
            );
          })}
          {!items.length && <div className="pos-menu-empty">No items match.</div>}
        </div>

        {/* Desktop: labeled table */}
        <div className="pos-menu-items-desktop table-responsive" data-testid="pos-menu-items-desktop">
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Menu</th>
                <th>Group</th>
                <th>Item</th>
                <th>Price</th>
                <th>Taxable</th>
                <th>86&apos;d</th>
                <th>Active</th>
                <th>Visible on POS</th>
                <th>Edit</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id}>
                  <td>{row.menuName}</td>
                  <td>{row.groupName}</td>
                  <td>
                    {row.name}
                    {row.openPrice ? (
                      <span className="badge bg-secondary ms-1">Open price</span>
                    ) : null}
                  </td>
                  <td style={{ minWidth: 96, maxWidth: 110 }}>
                    <input
                      className="form-control form-control-sm"
                      type="number"
                      step="0.01"
                      min="0"
                      aria-label={`Price for ${row.name}`}
                      defaultValue={row.price}
                      key={`price-${row.id}-${row.price}`}
                      disabled={busy}
                      onBlur={(e) => {
                        const next = Number(e.target.value);
                        if (Number.isFinite(next) && next !== row.price) {
                          patchMenuItem(row, { price: next });
                        }
                      }}
                    />
                  </td>
                  <td className="pos-menu-check-cell">
                    <label>
                      <input
                        type="checkbox"
                        checked={row.taxable}
                        disabled={busy}
                        onChange={(e) => patchItemFlags(row, { taxable: e.target.checked })}
                      />
                      Taxable
                    </label>
                  </td>
                  <td className="pos-menu-check-cell">
                    <label>
                      <input
                        type="checkbox"
                        checked={row.eightySixed}
                        disabled={busy}
                        onChange={(e) => patchItemFlags(row, { eightySixed: e.target.checked })}
                      />
                      86&apos;d
                    </label>
                  </td>
                  <td className="pos-menu-check-cell">
                    <label>
                      <input
                        type="checkbox"
                        checked={row.active}
                        disabled={busy}
                        onChange={(e) => patchMenuItem(row, { active: e.target.checked })}
                      />
                      Active
                    </label>
                  </td>
                  <td className="pos-menu-check-cell">
                    <label>
                      <input
                        type="checkbox"
                        checked={row.visibleOnPos}
                        disabled={busy}
                        onChange={(e) =>
                          patchMenuItem(row, { visibleOnPos: e.target.checked })
                        }
                      />
                      Visible on POS
                    </label>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => openEdit(row)}
                      disabled={busy}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={9} className="text-muted">
                    No items match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section style={{ marginBottom: 36 }}>
        <h3>Modifier groups</h3>
        <div className="table-responsive">
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Name</th>
                <th>Min</th>
                <th>Max</th>
                <th>Required</th>
                <th>Options</th>
              </tr>
            </thead>
            <tbody>
              {mods.map((g) => (
                <tr key={g.id}>
                  <td>{g.name}</td>
                  <td>{g.minSelections}</td>
                  <td>{g.maxSelections == null ? '—' : g.maxSelections}</td>
                  <td>{g.requiredMode || '—'}</td>
                  <td>{g.optionCount}</td>
                </tr>
              ))}
              {!mods.length && (
                <tr>
                  <td colSpan={5} className="text-muted">
                    No modifier groups yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section style={{ marginBottom: 36 }}>
        <h3>Prep stations &amp; printers</h3>
        <p className="text-muted">
          Epson ESC/POS network printers on the 10.10.10.x network. IP can be left blank until the
          printer is on the kitchen LAN.
        </p>
        <form onSubmit={createPrinter} className="row g-2 align-items-end" style={{ marginBottom: 16 }}>
          <div className="col-md-3">
            <label className="form-label">Printer name</label>
            <input
              className="form-control"
              value={printerForm.name}
              onChange={(e) => setPrinterForm({ ...printerForm, name: e.target.value })}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Host IP</label>
            <input
              className="form-control"
              value={printerForm.hostIp}
              onChange={(e) => setPrinterForm({ ...printerForm, hostIp: e.target.value })}
              placeholder="10.10.10.x (optional)"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Port</label>
            <input
              className="form-control"
              value={printerForm.port}
              onChange={(e) => setPrinterForm({ ...printerForm, port: e.target.value })}
            />
          </div>
          <div className="col-md-2">
            <button type="submit" className="btn btn-secondary w-100" disabled={busy}>
              Add printer
            </button>
          </div>
        </form>

        <div className="table-responsive" style={{ marginBottom: 20 }}>
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Printer</th>
                <th>Host IP</th>
                <th>Port</th>
              </tr>
            </thead>
            <tbody>
              {printers.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      defaultValue={p.hostIp || ''}
                      key={`ip-${p.id}-${p.hostIp || ''}`}
                      placeholder="10.10.10.x"
                      disabled={busy}
                      onBlur={(e) => {
                        if ((e.target.value || '') !== (p.hostIp || '')) {
                          savePrinterIp(p, e.target.value);
                        }
                      }}
                    />
                  </td>
                  <td>{p.port}</td>
                </tr>
              ))}
              {!printers.length && (
                <tr>
                  <td colSpan={3} className="text-muted">
                    No printers yet (import creates them from prep station mappings).
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="table-responsive">
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Prep station</th>
                <th>Printer</th>
              </tr>
            </thead>
            <tbody>
              {stations.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={s.printerId == null ? '' : String(s.printerId)}
                      disabled={busy}
                      onChange={(e) => linkStationPrinter(s.id, e.target.value)}
                    >
                      <option value="">None</option>
                      {printers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                          {p.hostIp ? ` (${p.hostIp})` : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
              {!stations.length && (
                <tr>
                  <td colSpan={2} className="text-muted">
                    No prep stations yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {editing && draft && (
        <div
          className="pos-menu-edit-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pos-menu-edit-title"
          data-testid="pos-menu-edit-sheet"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeEdit();
          }}
        >
          <div className="pos-menu-edit-sheet">
            <h3 id="pos-menu-edit-title" className="pos-menu-edit-sheet__title">
              {editing.name}
            </h3>
            <p className="pos-menu-edit-sheet__sub">
              {editing.menuName} · {editing.groupName}
              {editing.openPrice ? ' · Open price' : ''}
            </p>

            <div className="pos-menu-edit-field">
              <label className="form-label" htmlFor="pos-menu-edit-price">
                Price
              </label>
              <input
                id="pos-menu-edit-price"
                className="form-control"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={draft.price}
                disabled={busy}
                onChange={(e) => setDraft({ ...draft, price: e.target.value })}
              />
            </div>

            <div className="pos-menu-edit-checks">
              <label className="pos-menu-edit-check">
                <input
                  type="checkbox"
                  checked={draft.taxable}
                  disabled={busy}
                  onChange={(e) => setDraft({ ...draft, taxable: e.target.checked })}
                />
                Taxable
              </label>
              <label className="pos-menu-edit-check">
                <input
                  type="checkbox"
                  checked={draft.eightySixed}
                  disabled={busy}
                  onChange={(e) => setDraft({ ...draft, eightySixed: e.target.checked })}
                />
                86&apos;d
              </label>
              <label className="pos-menu-edit-check">
                <input
                  type="checkbox"
                  checked={draft.active}
                  disabled={busy}
                  onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                />
                Active
              </label>
              <label className="pos-menu-edit-check">
                <input
                  type="checkbox"
                  checked={draft.visibleOnPos}
                  disabled={busy}
                  onChange={(e) => setDraft({ ...draft, visibleOnPos: e.target.checked })}
                />
                Visible on POS
              </label>
            </div>

            <div className="pos-menu-edit-field">
              <div className="form-label">Modifiers</div>
              {editing.modifierGroups.length ? (
                <ul className="pos-menu-edit-list">
                  {editing.modifierGroups.map((g) => (
                    <li key={g.id}>{g.name}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                  No modifier groups on this placement.
                </p>
              )}
            </div>

            <div className="pos-menu-edit-field">
              <div className="form-label">Prep station</div>
              {editing.prepStations.length ? (
                <ul className="pos-menu-edit-list">
                  {editing.prepStations.map((s) => (
                    <li key={s.id}>{s.name}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                  No prep station linked.
                </p>
              )}
            </div>

            <div className="pos-menu-edit-actions">
              <button type="button" className="btn btn-outline-secondary" onClick={closeEdit} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={saveEdit} disabled={busy}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PosMenuAdmin;
